import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { nextButtonStyle, outerTabStyle, tabContentContainerStyle } from '../shared/styles';
import {
    CheckboxComponent,
    CheckboxFormFieldComponent,
    FieldHeaderComponent,
    fourAcrossStyle,
    oneAcrossStyle,
    TextAreaFormFieldComponent,
    TextFieldReadOnlyFormComponent,
    TextFormFieldComponent,
    twoAcrossStyle
} from '@vivli/shared/components';
import { Styles } from '@vivli/shared/theme';
import { first, timeout } from 'rxjs/operators';
import { useFormContext, useWatch } from 'react-hook-form';
import { ListingRequestStudySampleComponent } from '../listing-request-study-sample.component';
import { useListingRequestContext } from '@vivli/features/listing-request/infrastructure/context';
import { IListingRequest } from '@vivli/features/listing-request/infrastructure/interface';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';
import { ListingRequestStatusEnum } from '@vivli/features/listing-request/infrastructure/enum';
import { additionalInfoHint, funderEmailHint } from '@vivli/features/listing-request/infrastructure/constants';
import { getStudyAlreadyExistsMessage } from '@vivli/features/listing-request/infrastructure/hook';
import { useActiveUserContext, useModalApiContext, useStudiesApiContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { useNavigate } from 'react-router';
import { LrFundersRorContainerComponent } from '@vivli/features/organizations/components';
import { ListingRequestStudyNonNctComponent } from '../listing-request-study-nonNct.component';
import { ButtonView } from '@vivli/shared/views';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const nctRowStyle: CSSProperties = {
    ...Styles.FORM_ROW,
    justifyContent: 'start'
};
const studyButtonStyle: CSSProperties = {
    ...fourAcrossStyle,
    marginLeft: 10,
    height: '4em'
};
const studyCheckBoxStyle: CSSProperties = {
    backgroundColor: 'white',
    borderBottom: '2px solid white',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1px 20px, rgba(0, 0, 0, 0.2) 0px 1px 2px',
    maxWidth: '100%',
    marginBottom: '20px',
    padding: '15px 0'
};

const emptyStudy: IStudy = { id: '' };

export const YourStudyComponent = () => {
    const formApi = useFormContext<IListingRequest>();
    const navigate = useNavigate();
    const { isReadOnly, handleNext, listingRequest, setListingRequest } = useListingRequestContext();
    const studiesService = useStudiesApiContext();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const { hasPermission } = useActiveUserContext();
    const [sampleStudy, setSampleStudy] = useState(null);
    const [hasNonNctValue, sethasNonNctValue] = useState<boolean>(false);
    const [isLoadingSample, setIsLoadingSample] = useState(false);

    const { status, isAccepted, isApproved, isWithdrawn, isDraft } = useMemo(() => {
        const statusVal = listingRequest?.status ? ListingRequestStatusEnum[listingRequest?.status] : null;
        return {
            status: statusVal,
            isAccepted: statusVal ? statusVal === ListingRequestStatusEnum.StudyInCuration : false,
            isApproved: statusVal ? statusVal === ListingRequestStatusEnum.Approved : false,
            isWithdrawn: statusVal ? statusVal === ListingRequestStatusEnum.Withdrawn : false,
            isDraft: statusVal ? statusVal === ListingRequestStatusEnum.Draft : false
        };
    }, [listingRequest?.status]);

    const isCheckboxEnabled = useMemo(() => !isReadOnly, [isReadOnly]);

    const nctValue = useWatch({ name: 'datasets[0].nctId' });
    // const study: IStudy = listingRequest?.datasets.length > 0 ? listingRequest?.datasets[0] : null;
    const study = useMemo(() => {
        return listingRequest?.datasets.length > 0 ? listingRequest?.datasets[0] : null;
    }, [listingRequest]);

    const canEditRor = useMemo(() => !isReadOnly && !isWithdrawn, [isReadOnly, isWithdrawn]);
    const isVivliAdmin = useMemo(() => hasPermission(PermissionsEnum.SubmissionsActAdv), [hasPermission]);

    const handleError = (e) => {
        const msg =
            getStudyAlreadyExistsMessage(e) ??
            `Error looking up study by NCT ID; either the ID does not exist or clinicaltrials.gov did not respond - ${e}`;
        modalService.error(msg);
        //mark the nctId
        if (nctValue?.indexOf('error') == -1) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formApi.setValue('datasets[0].nctId', nctValue + '-error');
        }
        setSampleStudy(null);
        setIsLoadingSample(false);
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const handleViewStudy = () => {
        const studyUrl = `/admin/studies/${listingRequest?.datasetIds[0]}/admin-study-feature`;
        const confirmMessage = 'You are about to leave the form and will lose unsaved changes. Do you want to continue?';
        if (isReadOnly) {
            navigate(studyUrl);
            return;
        }

        modalService.confirm(confirmMessage, {
            title: 'Continue?',
            cancelText: 'Cancel',
            confirmText: 'OK',
            onConfirm: () => navigate(studyUrl)
        });
    };

    const timerRef = useRef(null);

    const toastAttemptingMessage = () => {
        toastService.info('Requesting NCT from clinicaltrials.gov');
    };

    const setIsListedOnCtgov = (value: boolean) => {
        sethasNonNctValue(value);
        if (listingRequest?.datasets[0]) {
            listingRequest.datasets[0].isListedOnCtgov = value;
        }
        //clear out fields if type of study changed
        mergeSampleWithCurrent({ id: '' }, true);
    };

    const playAttemptingMessage = () => {
        timerRef.current = setTimeout(toastAttemptingMessage, 50);
    };

    const handleSearch = (updateNeeded) => {
        if (!/^NCT\d{8}$/gi.test(nctValue)) {
            setSampleStudy(null);
            return;
        }
        if (!updateNeeded) {
            setSampleStudy(listingRequest?.datasets[0]);
            return;
        }
        setIsLoadingSample(true);
        playAttemptingMessage();
        const studyId = listingRequest?.datasets[0]?.id;
        studiesService
            .getSampleStudyForListingRequest(nctValue, studyId)
            .pipe(timeout(15000), first())
            .subscribe((sample) => {
                toastService.dismiss();
                clearTimeout(timerRef.current);
                setSampleStudy(sample);
                if (updateNeeded) {
                    mergeSampleWithCurrent(sample);
                }
                setIsLoadingSample(false);
            }, handleError);
    };

    const mergeSampleWithCurrent = (sampleStudy: IStudy, cleanNctId: boolean = false) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const currentStudy = formApi.getValues('datasets[0]') as IStudy;

        const mergedStudy = {
            ...sampleStudy,
            id: currentStudy.id,
            researchTeam: currentStudy.researchTeam,
            orgId: currentStudy.orgId,
            orgName: currentStudy.orgName,
            orgCode: currentStudy.orgCode,
            nctId: currentStudy.nctId,
            additionalInformation: currentStudy.additionalInformation,
            funder: currentStudy.funder,
            funderEmail: currentStudy.funderEmail,
            grant: currentStudy.grant,
            embargoDate: currentStudy.embargoDate
        };
        const cleanedStudy: IStudy = cleanNctId
            ? {
                  ...mergedStudy,
                  sponsorProtocolId: '',
                  nctId: '',
                  studyTitle: '',
                  extractedConditions: [],
                  extractedInterventions: [],
                  phase: undefined
              }
            : mergedStudy;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        formApi.setValue('datasets[0]', cleanedStudy, { shouldValidate: true });
    };

    const handleFundingOrgChange = (updatedStudy: IStudy) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        let studyWithAllChanges: IStudy = { ...formApi.getValues('datasets[0]') };
        studyWithAllChanges.fundingOrganizations = updatedStudy.fundingOrganizations || [];

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        formApi.setValue('datasets[0]', studyWithAllChanges, {
            shouldDirty: true,
            shouldValidate: true
        });
        formApi.trigger();

        //update the listing request context now
        const updated: IListingRequest = {
            ...listingRequest,
            datasets: [studyWithAllChanges]
        };
        setListingRequest(updated);
    };

    useEffect(() => {
        const updateNeeded = listingRequest?.datasets[0]?.nctId !== nctValue;
        if (!nctValue) {
            setSampleStudy(null);
            return;
        }
        handleSearch(updateNeeded);
    }, [nctValue]);

    useEffect(() => {
        if (listingRequest?.datasets[0]?.sponsorProtocolId && !listingRequest?.datasets[0]?.nctId) {
            sethasNonNctValue(true);
            if (listingRequest.datasets[0]) {
                listingRequest.datasets[0].isListedOnCtgov = false;
            }
        }
    }, []);

    return (
        <div style={outerTabStyle} className={'scrolly'}>
            <div style={tabContentContainerStyle}>
                <FieldHeaderComponent title={'TELL US ABOUT YOUR STUDY'} />
                <div>
                    Enter the registration ID from clinicaltrials.gov. This will bring in information about your study from
                    clinicaltrials.gov.
                    <p>
                        If you want to submit a study that has not been registered on clinicaltrials.gov, check the box that says “Study is
                        not Listed on ClinicalTrials.gov”.
                    </p>
                </div>

                {!isWithdrawn ? (
                    <div style={nctRowStyle}>
                        <CheckboxComponent
                            checked={hasNonNctValue}
                            key={'hasNonNctValue'}
                            onChange={() => setIsListedOnCtgov(!hasNonNctValue)}
                            label={'Study is not Listed on ClinicalTrials.gov'}
                        />

                        {hasNonNctValue ? (
                            <TextFormFieldComponent
                                name="datasets[0].sponsorProtocolId"
                                label={'Sponsor Protocol ID'}
                                key={'sponsorProtocolId'}
                                style={fourAcrossStyle}
                                readonly={isReadOnly || !isDraft}
                            />
                        ) : (
                            <TextFormFieldComponent
                                name={'datasets[0].nctId'}
                                label={'NCT ID (of the form NCT12345678)'}
                                key={'nctId'}
                                style={fourAcrossStyle}
                                readonly={isReadOnly || !isDraft}
                            />
                        )}

                        {(isAccepted || isApproved) && (
                            <ButtonView style={studyButtonStyle} onClick={handleViewStudy} isLoading={isLoadingSample}>
                                View Study Page
                            </ButtonView>
                        )}
                    </div>
                ) : (
                    <div style={Styles.FORM_ROW}>
                        {hasNonNctValue ? (
                            <TextFieldReadOnlyFormComponent
                                name="datasets[0].sponsorProtocolId"
                                label={'Sponsor Protocol ID'}
                                key={'sponsorProtocolId'}
                                style={fourAcrossStyle}
                                readonly={true}
                            />
                        ) : (
                            <TextFieldReadOnlyFormComponent
                                name={'datasets[0].nctId'}
                                label={'NCT ID (of the form NCT12345678)'}
                                key={'nctId'}
                                style={fourAcrossStyle}
                                readonly={true}
                            />
                        )}
                        <TextFieldReadOnlyFormComponent
                            label="Title"
                            style={twoAcrossStyle}
                            defaultValue={listingRequest?.withdrawnStudyTitle}
                            readonly={true}
                        />
                    </div>
                )}

                {!hasNonNctValue ? (
                    sampleStudy ? (
                        <ListingRequestStudySampleComponent sampleStudy={sampleStudy} />
                    ) : (
                        <ListingRequestStudySampleComponent sampleStudy={emptyStudy} />
                    )
                ) : (
                    <ListingRequestStudyNonNctComponent
                        isReadOnly={isReadOnly}
                        hasNonNctValue={hasNonNctValue}
                        sampleStudy={listingRequest?.datasets[0]}
                    />
                )}

                {isVivliAdmin && (
                    <div style={studyCheckBoxStyle}>
                        <CheckboxFormFieldComponent
                            name={'datasets[0].downloadableStudyIPDDataPackage'}
                            label={'Downloadable Study Data'}
                            reverseOrientation={true}
                            truncateLabel={false}
                            disabled={!isCheckboxEnabled}
                        />
                    </div>
                )}

                <div style={Styles.FORM_ROW}>
                    <TextAreaFormFieldComponent
                        name={'datasets[0].additionalInformation'}
                        label={
                            'Please include citations of any primary manuscripts and ' +
                            'include any additional information that may be helpful to a researcher when requesting this data.'
                        }
                        style={oneAcrossStyle}
                        hint={additionalInfoHint}
                        readonly={isReadOnly}
                        rows={3}
                    />

                    {(isVivliAdmin || isDraft || isReadOnly) && (
                        <TextFormFieldComponent
                            name={'datasets[0].funderEmail'}
                            label={'Please provide a contact name and email at your organization for invoicing'}
                            style={oneAcrossStyle}
                            hint={funderEmailHint}
                            readonly={isReadOnly}
                        />
                    )}
                    {study && <LrFundersRorContainerComponent study={study} onFunderChange={handleFundingOrgChange} canEdit={canEditRor} />}
                </div>

                <div style={Styles.FORM_ROW}>
                    <ButtonView style={nextButtonStyle} onClick={() => handleNext('DataSharingSettings')} isLoading={isLoadingSample}>
                        Next Page
                    </ButtonView>
                </div>
            </div>
        </div>
    );
};
